// S8 — per-camera recording schedule editor.
//
// Lives inside the RecordingsPage as the Schedule tab. Operators
// toggle between "always record" and "scheduled recording"; under the
// scheduled mode they edit per-day-of-week windows (max 10 total).
import { useEffect, useMemo, useState } from "react";
import { PlusIcon, TrashIcon } from "lucide-react";

import {
  type ScheduleWindow,
  usePutRecordingConfigS8,
  useRecordingConfigS8,
} from "@/api/playback";
import { useCameras } from "@/api/cameras";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";


const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_WINDOWS = 10;


function emptyWindow(day: number): ScheduleWindow {
  return { day, start: "09:00", end: "18:00" };
}


function HourGrid({ windows }: { windows: ScheduleWindow[] }) {
  // Render the 7×24 schedule heatmap so the operator sees the coverage
  // at a glance. Each cell = 1 hour for one day. Filled cells are
  // tinted orange.
  const filled = useMemo(() => {
    const grid: boolean[][] = Array.from(
      { length: 7 }, () => Array(24).fill(false) as boolean[],
    );
    for (const w of windows) {
      if (w.day < 0 || w.day > 6) continue;
      const [sh] = w.start.split(":").map(Number);
      const [eh, em] = w.end.split(":").map(Number);
      // Half-hour resolution would need more cells; for the preview we
      // round end up to the nearest hour so partial-hour windows still
      // get a visible cell.
      const endHour = em > 0 ? eh + 1 : eh;
      for (let h = sh; h < endHour && h < 24; h++) {
        grid[w.day][h] = true;
      }
    }
    return grid;
  }, [windows]);

  return (
    <div className="space-y-1">
      <div className="ml-12 flex text-[9px] text-muted-foreground">
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="flex-1 text-center">
            {h % 6 === 0 ? `${h}` : ""}
          </div>
        ))}
      </div>
      {DAY_LABELS.map((label, d) => (
        <div key={d} className="flex items-center gap-2">
          <span className="w-10 text-[11px] text-muted-foreground">{label}</span>
          <div className="flex flex-1 rounded border border-border bg-muted/40">
            {Array.from({ length: 24 }, (_, h) => (
              <div
                key={h}
                className={cn(
                  "flex-1 border-r border-border/40 last:border-r-0",
                  filled[d][h] ? "bg-[#ff6d00]/70" : "",
                )}
                style={{ height: 14 }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


export function RecordingScheduleEditor() {
  const cameras = useCameras({ limit: 200 });
  const [cameraId, setCameraId] = useState<string>("");
  const cfgQuery = useRecordingConfigS8(cameraId || null);
  const put = usePutRecordingConfigS8(cameraId);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!cfgQuery.data) {
      setScheduleEnabled(false);
      setWindows([]);
      return;
    }
    setScheduleEnabled(cfgQuery.data.schedule_enabled);
    setWindows(cfgQuery.data.schedule_windows ?? []);
  }, [cfgQuery.data]);

  useEffect(() => {
    if (!cameraId && cameras.data?.items?.length) {
      setCameraId(cameras.data.items[0].id);
    }
  }, [cameraId, cameras.data]);

  function addWindow(day: number) {
    if (windows.length >= MAX_WINDOWS) {
      setError(`Max ${MAX_WINDOWS} windows per camera`);
      return;
    }
    setWindows((prev) => [...prev, emptyWindow(day)]);
  }

  function removeWindow(idx: number) {
    setWindows((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateWindow(idx: number, patch: Partial<ScheduleWindow>) {
    setWindows((prev) =>
      prev.map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    );
  }

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await put.mutateAsync({
        schedule_enabled: scheduleEnabled,
        schedule_windows: windows,
      });
      setSaved(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to save schedule");
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Recording schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Label className="text-xs">Camera</Label>
            <select
              value={cameraId}
              onChange={(e) => setCameraId(e.target.value)}
              className="rounded border border-input bg-background px-2 py-1 text-sm"
            >
              {(cameras.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-xs">Mode</Label>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="radio"
                checked={!scheduleEnabled}
                onChange={() => setScheduleEnabled(false)}
              />
              Always record
            </label>
            <label className="flex items-center gap-1 text-xs">
              <input
                type="radio"
                checked={scheduleEnabled}
                onChange={() => setScheduleEnabled(true)}
              />
              Scheduled recording
            </label>
          </div>

          {scheduleEnabled && (
            <>
              <HourGrid windows={windows} />
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {DAY_LABELS.map((label, d) => {
                  const dayWindows = windows
                    .map((w, idx) => ({ w, idx }))
                    .filter(({ w }) => w.day === d);
                  return (
                    <div
                      key={d}
                      className="rounded border border-border p-2"
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs font-medium">{label}</span>
                        <button
                          type="button"
                          onClick={() => addWindow(d)}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Add window"
                          disabled={windows.length >= MAX_WINDOWS}
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {dayWindows.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">
                          No windows.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {dayWindows.map(({ w, idx }) => (
                            <div
                              key={idx}
                              className="flex items-center gap-1"
                            >
                              <Input
                                type="time"
                                value={w.start}
                                onChange={(e) =>
                                  updateWindow(idx, { start: e.target.value })
                                }
                                className="h-7 w-24 text-xs"
                              />
                              <span className="text-[10px] text-muted-foreground">
                                →
                              </span>
                              <Input
                                type="time"
                                value={w.end}
                                onChange={(e) =>
                                  updateWindow(idx, { end: e.target.value })
                                }
                                className="h-7 w-24 text-xs"
                              />
                              <button
                                type="button"
                                onClick={() => removeWindow(idx)}
                                className="rounded p-1 text-muted-foreground hover:text-red-500"
                                title="Remove"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {error && (
            <div className="rounded bg-red-50 px-2 py-1 text-xs text-red-700">
              {error}
            </div>
          )}
          {saved && (
            <div className="rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
              Saved.
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={() => void save()}
              disabled={put.isPending || !cameraId}
            >
              {put.isPending ? "Saving…" : "Save schedule"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
